import { z } from 'zod';
import { widgetDefinitionSchema } from './widgets.js';

/**
 * `plugin.json` — the static half of a plugin. The core reads this without
 * executing any plugin code, so it can list, enable and describe plugins that
 * fail to load.
 */
export const pluginManifestSchema = z.object({
  id: z
    .string()
    .regex(/^[a-z][a-z0-9-]{1,40}$/, 'plugin id must be kebab-case'),
  name: z.string().min(1),
  version: z.string().default('0.0.0'),
  description: z.string().default(''),
  author: z.string().default(''),
  /** Entry module, relative to the plugin directory. */
  main: z.string().default('src/index.ts'),
  /** Semver range of the host API this plugin was written against. */
  apiVersion: z.string().default('1.x'),
  /** Lucide icon name shown in the sidebar. */
  icon: z.string().default('box'),
  /** Sidebar ordering; lower sorts first. */
  order: z.number().int().default(100),
  /** Other plugin ids that must load first. */
  dependencies: z.array(z.string()).default([]),
  /**
   * Capabilities the plugin needs. The host refuses to load a plugin that uses
   * an API it did not declare, so an operator can see the blast radius before
   * enabling a third-party plugin.
   */
  permissions: z
    .array(
      z.enum([
        'resources:read',
        'resources:write',
        'metrics:write',
        'alerts:raise',
        'tickets:write',
        'billing:write',
        'network:outbound',
        'exec:shell',
        'storage',
        'http:routes',
        'schedule',
        'events:subscribe',
        'vault:reveal',
        'resources:reveal-access',
        'host:firewall',
      ]),
    )
    .default([]),
  /** Resource kinds this plugin owns, for the "add resource" picker. */
  resourceKinds: z
    .array(
      z.object({
        kind: z.string(),
        label: z.string(),
        icon: z.string().default('box'),
        /** Fields shown in the add/edit resource form. */
        configFields: z
          .array(
            z.object({
              key: z.string(),
              label: z.string(),
              type: z.enum(['string', 'number', 'boolean', 'password', 'select', 'textarea']),
              required: z.boolean().default(false),
              default: z.unknown().optional(),
              placeholder: z.string().optional(),
              help: z.string().optional(),
              choices: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
              /** Encrypted at rest and never returned by the API. */
              secret: z.boolean().default(false),
            }),
          )
          .default([]),
        defaultPollIntervalSec: z.number().int().positive().default(60),
      }),
    )
    .default([]),
  widgets: z.array(widgetDefinitionSchema).default([]),
  /** Plugin-level settings, edited on the plugin's settings page. */
  settings: z
    .array(
      z.object({
        key: z.string(),
        label: z.string(),
        type: z.enum(['string', 'number', 'boolean', 'password', 'select', 'textarea']),
        default: z.unknown().optional(),
        help: z.string().optional(),
        secret: z.boolean().default(false),
        choices: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
      }),
    )
    .default([]),
  /** Extra nav entries the plugin contributes, beyond its default page. */
  navigation: z
    .array(z.object({ path: z.string(), label: z.string(), icon: z.string().default('box') }))
    .default([]),
});
export type PluginManifest = z.infer<typeof pluginManifestSchema>;

export const PLUGIN_STATES = ['loaded', 'disabled', 'error', 'unloaded'] as const;
export type PluginState = (typeof PLUGIN_STATES)[number];

export interface PluginStatus {
  manifest: PluginManifest;
  state: PluginState;
  error: string | null;
  loadedAt: number | null;
  /** Resource count owned by this plugin. */
  resourceCount: number;
  /** Rolling collector health, so a broken integration is visible. */
  lastCollectionAt: number | null;
  lastCollectionOk: boolean;
  collectionErrors: number;
}

/** Result of one collection run for one resource. */
export interface CollectionResult {
  health: 'up' | 'degraded' | 'down' | 'maintenance' | 'unknown';
  statusMessage?: string;
  /** Merged into the resource's `attributes`. */
  attributes?: Record<string, unknown>;
  metrics?: Array<{
    metric: string;
    value: number;
    unit?: string;
    labels?: Record<string, string>;
  }>;
  /** Ad-hoc alerts the plugin wants raised regardless of rules. */
  alerts?: Array<{
    dedupeKey: string;
    severity: 'info' | 'warning' | 'critical';
    title: string;
    description?: string;
    /** Omit to leave the alert open; set true to resolve it. */
    resolved?: boolean;
  }>;
}
